import type { ReactNode } from 'react';

/**
 * Palabras tocables de la burbuja del alumno, dibujadas por tramos: la burbuja
 * mezcla texto suelto con subrayados de corrección (`annotateText`), y cada
 * tramo `[lo,hi)` se dibuja con `renderTapRange`. Las palabras se numeran sobre
 * el texto ENTERO (las mismas que `useWordSelection` corta por espacios), así
 * una frase puede cruzar de un tramo a otro. Mismas clases que `SpokenBody`:
 * el resaltado de la selección se ve igual que en la burbuja del coach.
 */

export interface TapToken {
  text: string;
  start: number;
  end: number;
  ws: boolean;
  /** Palabra: su número. Espacio: el número de la palabra anterior (-1 al principio). */
  widx: number;
}

export function tokenizeForTap(text: string): TapToken[] {
  const out: TapToken[] = [];
  let cursor = 0;
  let widx = -1;
  for (const part of text.split(/(\s+)/)) {
    if (part === '') continue;
    const ws = /^\s+$/.test(part);
    if (!ws) widx += 1;
    out.push({ text: part, start: cursor, end: cursor + part.length, ws, widx });
    cursor += part.length;
  }
  return out;
}

export interface TapRangeOpts {
  /** Las palabras de este tramo se pueden tocar (no dentro de un subrayado: ése abre la corrección). */
  tappable: boolean;
  /** Rango seleccionado [start, end] en números de palabra (inclusivo). */
  selRange: readonly [number, number] | null;
  onWordTap: (text: string, el: HTMLElement, wordIdx: number) => void;
}

export function renderTapRange(
  tokens: ReadonlyArray<TapToken>,
  lo: number,
  hi: number,
  { tappable, selRange, onWordTap }: TapRangeOpts,
): ReactNode[] {
  const inRange = (i: number): boolean => !!selRange && i >= selRange[0] && i <= selRange[1];
  const isMulti = !!selRange && selRange[1] > selRange[0];
  const out: ReactNode[] = [];
  for (const t of tokens) {
    if (t.end <= lo || t.start >= hi) continue;
    // Una palabra cortada por el borde de un subrayado (el corrector marcó
    // media palabra): ese pedazo va como texto, sin toque.
    if (t.start < lo || t.end > hi) {
      out.push(
        <span key={t.start}>
          {t.text.slice(Math.max(lo, t.start) - t.start, Math.min(hi, t.end) - t.start)}
        </span>,
      );
      continue;
    }
    if (t.ws) {
      // Espacio entre dos palabras de la frase elegida: se pinta, así la frase
      // se lee como una banda continua.
      const gap = inRange(t.widx) && inRange(t.widx + 1);
      out.push(
        <span key={t.start} className={gap ? 'spoken-tok is-selected is-sel-gap' : undefined}>
          {t.text}
        </span>,
      );
      continue;
    }
    const cls = ['spoken-tok'];
    if (tappable) cls.push('is-tappable');
    if (inRange(t.widx)) {
      cls.push('is-selected');
      if (!isMulti || !selRange) cls.push('is-sel-solo');
      else if (t.widx === selRange[0]) cls.push('is-sel-start');
      else if (t.widx === selRange[1]) cls.push('is-sel-end');
      else cls.push('is-sel-mid');
    }
    const widx = t.widx;
    out.push(
      <span
        key={t.start}
        className={cls.join(' ')}
        data-widx={widx}
        {...(tappable
          ? {
              onClick: (e: React.MouseEvent<HTMLSpanElement>) => {
                e.stopPropagation();
                onWordTap(t.text, e.currentTarget, widx);
              },
            }
          : {})}
      >
        {t.text}
      </span>,
    );
  }
  return out;
}
